
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/admin/AdminLayout';
import { useAdminSettings } from '@/hooks/useAdminSettings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import FormSettingsSection from '@/components/admin/signupForm/FormSettingsSection';
import { SignupFormConfig } from '@/schemas/signupFormSchema';
import { getSignupForm, saveSignupForm } from '@/utils/config/signupForm';
import { useToast } from '@/hooks/use-toast';

const AdminSignupForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { user, handleLogout } = useAdminSettings();

  const defaultFormConfig: SignupFormConfig = {
    formTitle: "Referral Program Signup",
    fields: [
      { id: "1", name: "name", email: "name@field.com", label: "Full Name", type: "text", visible: true, required: true, defaultValue: "" },
      { id: "2", name: "email", email: "email@field.com", label: "Email", type: "email", visible: true, required: true, defaultValue: "" },
      { id: "3", name: "phone", email: "phone@field.com", label: "Phone Number", type: "tel", visible: true, required: true, defaultValue: "" },
      { id: "4", name: "country", email: "country@field.com", label: "Country", type: "text", visible: true, required: true, defaultValue: "USA" },
      { id: "5", name: "password", email: "password@field.com", label: "Password", type: "password", visible: true, required: true, defaultValue: "" },
      { id: "6", name: "confirmPassword", email: "confirmPassword@field.com", label: "Confirm Password", type: "password", visible: true, required: true, defaultValue: "" },
      { id: "7", name: "terms", email: "terms@field.com", label: "Terms and Conditions", type: "checkbox", visible: true, required: true, defaultValue: "" }
    ],
    ccEmail: "",
    enableCcEmail: false,
    includeTimestamp: true,
    validateAccountNumber: false,
    validateLicenseNumber: false
  };

  const [formConfig, setFormConfig] = useState<SignupFormConfig>(defaultFormConfig);

  useEffect(() => {
    const loadFormConfig = async () => {
      setIsLoading(true);
      try {
        const savedForm = await getSignupForm();
        if (savedForm) {
          setFormConfig(savedForm);
        }
      } catch (error) {
        console.error("Error loading form configuration:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadFormConfig();
  }, []);

  const handleSaveForm = async () => {
    setIsSaving(true);
    try {
      await saveSignupForm(formConfig);
      toast({
        title: "Form Saved",
        description: "The signup form configuration has been saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save the form configuration.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout isLoading={isLoading} onLogout={handleLogout} userRole={user?.role}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/settings')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Settings
          </Button>
          
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Signup Form Management</h1>
            {user?.role && (
              <span className="ml-3 px-3 py-1 text-xs font-semibold text-white rounded-full bg-purple-600">
                {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </span>
            )}
          </div>
          <p className="text-gray-600">Customize the referral program signup form</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Form Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <FormSettingsSection 
              formConfig={formConfig}
              setFormConfig={setFormConfig}
              onSave={handleSaveForm}
              isSaving={isSaving}
            />
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminSignupForm;
